import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Grid,
  Paper,
  Box,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  generateUtilityClasses,
  TableBody,
  IconButton,
  CircularProgress,
  Typography,
  Popover,
  useTheme,
  MenuItem,
} from '@mui/material'
import { styled } from '@mui/system'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../../../utils/sorting'
import { storageGet, storageSet } from '../../../../../../utils/storage'
import { UspFormPackViewResult } from '../../../../../../models'
import ConfirmDialog from '../../../../../../components/ConfirmDialog'
import { useDeleteFormPackMutation } from '../../../../../../services'
import { enqueueNotification } from '../../../../../../redux/slices/notificationsSlice'
import { useDispatch } from 'react-redux'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useNavigate } from 'react-router-dom'
import { appRoute } from '../../../../../../constants'
import { setFormsPacks } from '../../../../../../redux/slices/formPacksSlice'
import Iconify from '../../../../../../components/iconify'
import CustomPopover from '../../../../../../components/custom-popover/custom-popover'
import { usePopover } from '../../../../../../components/custom-popover'
import Label from '../../../../../../components/label'

const classes = generateUtilityClasses('Grid', ['root', 'tableHead', 'activeItem', 'inactiveItem'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
}))

interface Props {
  loading: boolean
  setRowData: Dispatch<SetStateAction<UspFormPackViewResult>>
  data: UspFormPackViewResult[]
  setData: Dispatch<SetStateAction<UspFormPackViewResult[]>>
  setOpenAddEditModal: Dispatch<SetStateAction<boolean>>
  handleOpenModal: () => void
  setType: Dispatch<SetStateAction<string>>
}

const FormsPacksTableBlock = ({ data, loading, setData, setOpenAddEditModal, setType, setRowData }: Props) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [deleteFormPack, { isSuccess: deleteFormPackIsSuccess }] = useDeleteFormPackMutation()
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ formPackId: string } | undefined>(undefined)
  const initialOrder = (storageGet('forms_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('forms_order_by') || 'Vendor') as keyof UspFormPackViewResult
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof UspFormPackViewResult>(initialOrderBy)
  const [openPopoverId, setOpenPopoverId] = useState<null | number>(null)
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspFormPackViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('forms_order', isAsc ? 'desc' : 'asc')
    storageSet('forms_order_by', property)
  }

  const createSortHandler = (property: keyof UspFormPackViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = async (): Promise<void> => {
    if (deleteConfirmDialog?.formPackId) {
      await deleteFormPack(deleteConfirmDialog)
    }
    setDeleteConfirmDialog(undefined)
  }

  const handleMoreVertClick = (event: React.MouseEvent<HTMLElement>, item: UspFormPackViewResult) => {
    setOpenPopoverId(item?.FormPackID)
  }

  const handleOpen = () => {
    setType('edit')
    setOpenAddEditModal(true)
  }

  useEffect(() => {
    if (deleteConfirmDialog?.formPackId) {
      if (deleteFormPackIsSuccess) {
        let updatedData: UspFormPackViewResult[]
        updatedData = data?.map((item: UspFormPackViewResult) =>
          item.FormPackID?.toString() === deleteConfirmDialog.formPackId ? { ...item, IsActive: false } : item,
        )
        setData(updatedData)
        dispatch(enqueueNotification({ message: 'Success' }))
      }
    }
  }, [deleteFormPackIsSuccess])

  return (
    <>
      <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 118px)' }} className={classes.root}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'State' ? order : false}>
                <TableSortLabel active={orderBy === 'State'} direction={orderBy === 'State' ? order : 'asc'} onClick={createSortHandler('State')}>
                  State
                  {orderBy === 'State' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'Name' ? order : false}>
                <TableSortLabel active={orderBy === 'Name'} direction={orderBy === 'Name' ? order : 'asc'} onClick={createSortHandler('Name')}>
                  Name
                  {orderBy === 'Name' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), data as any[])?.map((item: any, index: number) => (
              <TableRow key={item.FormPackID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{item?.State}</TableCell>
                <TableCell>{item?.Name}</TableCell>
                <TableCell>
                  <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                </TableCell>

                <TableCell align={'right'}>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setSelected(item)
                      popover.onOpen(event)
                    }}
                  >
                    <Iconify icon='eva:more-vertical-fill' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Are you sure you want to delete this form pack?'}
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
        <MenuItem
          onClick={(e) => {
            dispatch(setFormsPacks(selected))
            navigate(appRoute.SettingsFormsFormList.replace(':formPackId', selected?.FormPackID?.toString() as string))

            popover.onClose()
          }}
        >
          {/* <Iconify icon='solar:pen-bold' /> */}
          Form List
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            handleOpen()
            setRowData(selected as any)
            popover.onClose()
          }}
        >
          <Iconify icon='solar:pen-bold' />
          Edit
        </MenuItem>
        <MenuItem
          disabled={selected?.IsActive ? false : true}
          onClick={() => {
            setDeleteConfirmDialog({ formPackId: selected.FormPackID?.toString() as string })
            setOpenPopoverId(null)
            popover.onClose()
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon='solar:trash-bin-trash-bold' />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  )
}

export default FormsPacksTableBlock
